import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Loader, Search, Power, PowerOff, KeyRound, Eye, EyeOff, Cpu, Pencil, RefreshCw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from '@/lib/validators';
import api from "@/lib/api";

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';
import PhoneMachinesList from './Machines/PhoneMachinesList';
import PhoneMachineForm from './Machines/PhoneMachineForm';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * The plant's machines as a list.
 *
 * A card grid showed nine presses at a time and hid the two things a supervisor
 * looks these up for: what a machine is linked to, and how much is queued on it.
 * Both are columns now, and the queue is sortable — which turns the page from a
 * directory into something you can actually schedule against.
 *
 * Add and edit share one side pane. Deactivation keeps a confirmation, but it is
 * the app's own dialog rather than `window.confirm`, which blocks the whole
 * browser tab and cannot be styled or dismissed by the page.
 */

const EMPTY = { machine_name: "", username: "", password: "", linked_stage_ids: [], notes: "" };

export default function MachinesPage() {
  const isMobile = useIsMobile();
  const [machines, setMachines] = useState([]);
  const [allStages, setAllStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);

  const [pane, setPane] = useState(null); // 'add' | machine
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [resetFor, setResetFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmToggle, setConfirmToggle] = useState(null);

  const fetchMachines = useCallback(async () => {
    try {
      setLoading(true);
      const params = { active_only: !showInactive };
      if (stageFilter !== "all") params.stage = stageFilter;
      const { data } = await api.get("/admin/machines", { params });
      setMachines(data.machines || []);
    } catch {
      toast.error("Failed to load machines");
    } finally {
      setLoading(false);
    }
  }, [stageFilter, showInactive]);

  const fetchStages = useCallback(async () => {
    try {
      const { data } = await api.get("/stages", { params: { is_active: true } });
      const stages = Array.isArray(data) ? data : (data.stages || []);
      setAllStages(stages.map(s => ({
        id: s.id, stage_name: s.name || s.stage_name, category: s.category, slug: s.slug,
      })));
    } catch {
      try {
        const { data } = await api.get("/admin/machines/config/production-stages");
        setAllStages(data.stages || []);
      } catch { setAllStages([]); }
    }
  }, []);

  useEffect(() => { fetchMachines(); }, [fetchMachines]);
  useEffect(() => { fetchStages(); }, [fetchStages]);

  const openAdd = () => { setForm(EMPTY); setShowPassword(false); setPane('add'); };
  const openEdit = (machine) => {
    setForm({
      machine_name: machine.machine_name,
      username: machine.username || "",
      password: "",
      linked_stage_ids: machine.linked_stage_ids || [],
      notes: machine.notes || "",
    });
    setShowPassword(false);
    setPane(machine);
  };

  const save = async () => {
    if (!form.machine_name) { toast.error("Machine name is required"); return; }
    if (pane === 'add' && !form.username) { toast.error("Username is required"); return; }
    if (pane === 'add' && !form.password) { toast.error("Password is required"); return; }

    setSubmitting(true);
    try {
      if (pane === 'add') {
        await api.post("/admin/machines", form);
        toast.success("Machine created");
      } else {
        await api.put(`/admin/machines/${pane.id}`, {
          machine_name: form.machine_name,
          linked_stage_ids: form.linked_stage_ids,
          notes: form.notes,
        });
        toast.success("Machine updated");
      }
      setPane(null);
      setForm(EMPTY);
      fetchMachines();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save machine'));
    }
    setSubmitting(false);
  };

  const toggleStatus = async () => {
    const machine = confirmToggle;
    setConfirmToggle(null);
    try {
      const res = await api.patch(`/admin/machines/${machine.id}/toggle-status`);
      toast.success(res.data.message);
      fetchMachines();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change machine status");
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/admin/machines/${resetFor.id}/reset-password`, { new_password: newPassword });
      toast.success("Password reset");
      setResetFor(null);
      setNewPassword("");
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to reset password'));
    }
  };

  const toggleStage = (stageId) => setForm(prev => ({
    ...prev,
    linked_stage_ids: prev.linked_stage_ids.includes(stageId)
      ? prev.linked_stage_ids.filter(s => s !== stageId)
      : [...prev.linked_stage_ids, stageId],
  }));

  const stageName = useCallback(
    (stageId) => allStages.find(s => s.id === stageId)?.stage_name || stageId,
    [allStages]
  );

  const visible = useMemo(() => {
    if (!searchQuery) return machines;
    const q = searchQuery.toLowerCase();
    return machines.filter(m =>
      m.machine_name?.toLowerCase().includes(q) ||
      m.username?.toLowerCase().includes(q) ||
      (m.linked_stage_names || []).some(s => String(s).toLowerCase().includes(q)) ||
      (m.linked_stage_ids || []).some(id => stageName(id).toLowerCase().includes(q)));
  }, [machines, searchQuery, stageName]);

  const [sort, setSort] = useState(null);
  const rows = useMemo(() => {
    if (!sort?.key) return visible;
    return [...visible].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
      return sort.dir === 'desc' ? -cmp : cmp;
    });
  }, [visible, sort]);

  const columns = useMemo(() => ([
    {
      key: 'machine_name',
      header: 'Machine',
      width: 'minmax(12rem,1.3fr)',
      sortable: true,
      render: (m) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{m.machine_name}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{m.username || '—'}</span>
        </>
      ),
    },
    {
      key: 'stages',
      header: 'Runs',
      width: 'minmax(14rem,1.6fr)',
      render: (m) => {
        const names = m.linked_stage_names?.length
          ? m.linked_stage_names
          : (m.linked_stage_ids || []).map(stageName);
        if (!names.length) return <span className="text-slate-400">Not linked to a stage</span>;
        return (
          <span className="flex flex-wrap gap-1">
            {names.slice(0, 4).map((s, i) => (
              <span key={`${s}-${i}`} className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">{s}</span>
            ))}
            {names.length > 4 && <span className="text-[10px] text-slate-400">+{names.length - 4}</span>}
          </span>
        );
      },
    },
    {
      key: 'queue_count',
      header: 'Queue',
      align: 'right',
      width: '6rem',
      sortable: true,
      render: (m) => (
        <span className={`font-semibold ${m.queue_count ? 'text-brand-blue' : 'text-slate-400'}`}>
          {m.queue_count || 0}
        </span>
      ),
      total: (list) => list.reduce((s, m) => s + (m.queue_count || 0), 0),
    },
    {
      key: 'notes',
      header: 'Notes',
      width: 'minmax(8rem,1fr)',
      render: (m) => (
        <span className="block truncate text-[12px] text-slate-500" title={m.notes}>{m.notes || '—'}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '6rem',
      render: (m) => (
        <span className={`text-[12px] font-medium ${m.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`}>
          {m.status || 'active'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '6rem',
      align: 'right',
      render: (m) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton icon={Pencil} label="Edit machine" iconOnly onClick={() => openEdit(m)} />
          <CellButton icon={KeyRound} label="Reset password" iconOnly
            onClick={() => { setResetFor(m); setNewPassword(''); }} />
          <CellButton
            icon={m.status === 'active' ? PowerOff : Power}
            label={m.status === 'active' ? 'Deactivate machine' : 'Activate machine'}
            iconOnly
            tone={m.status === 'active' ? 'danger' : 'good'}
            onClick={() => setConfirmToggle(m)}
          />
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [stageName]);

  const activeCount = machines.filter(m => m.status !== 'inactive').length;
  const linkedCount = machines.filter(m => (m.linked_stage_ids?.length || m.linked_stage_names?.length)).length;

  const closeForm = () => { setPane(null); setForm(EMPTY); };

  if (loading && machines.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-brand-blue" />
      </div>
    );
  }

  const confirmDialog = (
    <AlertDialog open={!!confirmToggle} onOpenChange={() => setConfirmToggle(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {confirmToggle?.status === 'active' ? 'Deactivate' : 'Activate'} {confirmToggle?.machine_name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {confirmToggle?.status === 'active'
              ? 'Work already queued on this machine will need reassigning before it can run.'
              : 'The machine becomes available for assignment again.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={toggleStatus}
            className={confirmToggle?.status === 'active' ? 'bg-red-600 text-white hover:bg-red-700' : ''}
          >
            {confirmToggle?.status === 'active' ? 'Deactivate' : 'Activate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // one card per machine below lg; the form takes the whole screen while it is
  // open, and the deactivate confirmation is the same dialog either way
  if (isMobile) {
    if (pane) {
      return (
        <PhoneMachineForm
          mode={pane} form={form} onChange={setForm} allStages={allStages}
          showPassword={showPassword} setShowPassword={setShowPassword}
          submitting={submitting} onSave={save} onCancel={closeForm}
        />
      );
    }
    return (
      <>
        <PhoneMachinesList
          machines={rows} total={machines.length} loading={loading}
          onSearch={setSearchQuery}
          allStages={allStages} stageFilter={stageFilter} onStageFilter={setStageFilter}
          showInactive={showInactive} onShowInactive={setShowInactive}
          stageName={stageName}
          onNew={openAdd} onEdit={openEdit}
          onReset={(m) => { setResetFor(m); setNewPassword(''); }}
          onToggle={setConfirmToggle}
          resetFor={resetFor} newPassword={newPassword} setNewPassword={setNewPassword}
          onResetSubmit={resetPassword} onResetClose={() => { setResetFor(null); setNewPassword(''); }}
        />
        {confirmDialog}
      </>
    );
  }

  return (
    <Screen data-testid="machines-page">
      <CommandBar
        title="Machines"
        subtitle={`${machines.length} on the floor`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchMachines, disabled: loading },
          { key: 'add', label: 'Add Machine', icon: Plus, tone: 'accent', onClick: openAdd },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Machine, username, stage…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: 'machines', value: machines.length, tone: 'blue' },
          { key: null, label: 'active', value: activeCount, tone: 'good' },
          { key: null, label: 'linked to a stage', value: linkedCount, tone: linkedCount < machines.length ? 'warn' : 'default' },
          { key: null, label: 'queued jobs', value: machines.reduce((s, m) => s + (m.queue_count || 0), 0) },
        ]}
      >
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Stage</span>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All stages</SelectItem>
            {allStages.map(s => (
              <SelectItem key={s.id} value={s.stage_name} className="text-xs">{s.stage_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setShowInactive(v => !v)}
          aria-pressed={showInactive}
          className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
            showInactive
              ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30'
              : 'text-slate-500 hover:bg-slate-100'
          }`}
        >
          Show inactive
        </button>
      </StatStrip>

      <Pane title="Machines" aside={`${rows.length} shown`} scroll={false} bodyClassName="flex flex-col">
        <DataGrid
          data-testid="machines-table"
          columns={columns}
          rows={rows}
          loading={loading}
          getRowId={(m) => m.id}
          sort={sort}
          onSortChange={setSort}
          showTotals={rows.length > 0}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Cpu className="h-8 w-8 opacity-50" />
              No machines found
            </span>
          }
        />
      </Pane>

      <SidePane
        open={!!pane}
        onClose={closeForm}
        title={pane === 'add' ? 'Add machine' : `Edit ${pane?.machine_name || 'machine'}`}
        subtitle={pane === 'add' ? 'Operators sign in on the tablet with these credentials' : pane?.username}
        width="26rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={closeForm}>Cancel</Button>
            <Button size="sm" disabled={submitting} onClick={save}
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {submitting ? 'Saving…' : pane === 'add' ? 'Create machine' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Machine name *</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="e.g. Komori-6C"
              value={form.machine_name}
              onChange={e => setForm(f => ({ ...f, machine_name: e.target.value }))} />
          </label>

          {pane === 'add' && (
            <>
              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Username *</span>
                <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-[11px] font-medium text-slate-600">Password *</span>
                <span className="relative mt-0.5 block">
                  <Input
                    className="h-8 pr-8 text-sm"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
                  >
                    {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </span>
              </label>
            </>
          )}

          <div>
            <span className="text-[11px] font-medium text-slate-600">
              Runs which stages
              {form.linked_stage_ids.length > 0 && (
                <span className="ml-1 text-slate-400">({form.linked_stage_ids.length} selected)</span>
              )}
            </span>
            <div className="mt-0.5 max-h-52 space-y-0.5 overflow-y-auto rounded border border-slate-200 bg-white p-1">
              {allStages.length === 0 && <p className="p-1 text-xs text-slate-400">No stages available</p>}
              {allStages.map(stage => {
                const on = form.linked_stage_ids.includes(stage.id);
                return (
                  <button
                    key={stage.id}
                    type="button"
                    onClick={() => toggleStage(stage.id)}
                    aria-pressed={on}
                    className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-slate-50 ${on ? 'bg-blue-50' : ''}`}
                  >
                    <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                      on ? 'border-brand-blue bg-brand-blue' : 'border-slate-300'
                    }`}>
                      {on && <Check className="h-2.5 w-2.5 text-white" />}
                    </span>
                    <span className="flex-1 truncate text-[12px] text-slate-700">{stage.stage_name}</span>
                    {stage.category && (
                      <Badge variant="outline" className="shrink-0 px-1 py-0 text-[9px] text-slate-500">
                        {stage.category}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Notes</span>
            <Textarea className="mt-0.5 resize-none text-sm" rows={2} placeholder="Capacity, quirks, service dates…"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </label>
        </div>
      </SidePane>

      <SidePane
        open={!!resetFor}
        onClose={() => { setResetFor(null); setNewPassword(''); }}
        title={`Reset password — ${resetFor?.machine_name || ''}`}
        subtitle="The operator will need the new password on their next sign-in"
        width="22rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => { setResetFor(null); setNewPassword(''); }}>Cancel</Button>
            <Button size="sm" onClick={resetPassword} className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              Reset password
            </Button>
          </>
        }
      >
        <div className="space-y-2 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">New password</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" type="text" value={newPassword}
              placeholder="At least 6 characters"
              onChange={e => setNewPassword(e.target.value)} />
          </label>
        </div>
      </SidePane>

      {confirmDialog}
    </Screen>
  );
}
